// get_entry_group builds one card per grouping key, and a card can hold the same show more than
// once (two entries of one animal in one show with identical key values). The card UI assumes one
// entry per show, so those extra entries are split off into cards of their own: the last entry for
// a show stays on the original card and each earlier one becomes a one-show card.
//
// A card's per-show arrays are parallel (get_entry_group aggregates them in one pass), so every
// split takes the same index out of all of them. `rows` (SHO-439) carries entry_id, so it is
// matched by id rather than by position.
//
// The card-level values (name, class, entry_details, check-in and dates) are the first entry's
// or aggregates over all of them, so each split card takes them from its own rows. Otherwise one
// animal in two classes of a show shows the first class and details on both cards, and an edit
// writes the first entry's class onto the other.
export function splitDuplicateShows (entryGroup) {
  if (!entryGroup || !Array.isArray(entryGroup.entries)) return entryGroup
  const cards = []
  entryGroup.entries.forEach(card => {
    const shows = card.shows || []
    const lastIndex = {}
    shows.forEach((s, i) => { lastIndex[s] = i })
    const splitIdx = shows.map((s, i) => i).filter(i => lastIndex[shows[i]] !== i)
    if (!splitIdx.length) {
      cards.push(card)
      return
    }
    splitIdx.forEach(i => cards.push(withOwnRowValues(pickIndexes(card, [i]))))
    const keepIdx = shows.map((s, i) => i).filter(i => lastIndex[shows[i]] === i)
    cards.push(withOwnRowValues(pickIndexes(card, keepIdx)))
  })
  entryGroup.entries = cards
  return entryGroup
}

const latest = times => times.filter(Boolean).reduce((a, b) => (!a || new Date(b) > new Date(a) ? b : a), null)

// A card's first-entry values and aggregates, recomputed from the rows it now holds. Without rows
// (older payloads) the card is left as it is.
function withOwnRowValues (card) {
  const rows = card.rows || []
  const first = rowFor(card, 0)
  if (!first) return card
  // the class object is looked up for the card's first class; it only fits while that's the same
  if (card.class_id !== first.class_id) card.class = null
  card.class_id = first.class_id
  card.entry_details = JSON.parse(JSON.stringify(first.entry_details || {}))
  const name = (first.entry_details || {}).name
  card.name = name === undefined ? null : name
  card.checked_in = latestCheckIn(card)
  card.created_at = latest(rows.map(r => r.created_at))
  card.last_updated_at = latest(rows.map(r => r.last_updated_at))
  return card
}

// weights / checked_ins are per-show arrays the check-in list builds on the card
const PARALLEL_KEYS = ['shows', 'entry_ids', 'entry_numbers', 'weights', 'checked_ins']

function pickIndexes (card, idx) {
  const out = JSON.parse(JSON.stringify(card))
  PARALLEL_KEYS.forEach(k => {
    // an array can trail shows: a show just added has no entry id or number until it's saved
    if (Array.isArray(card[k]) && card[k].length <= card.shows.length) {
      out[k] = idx.filter(i => i < card[k].length).map(i => card[k][i])
    }
  })
  if (card.show_entries && Array.isArray(card.show_entries.show_ids)) {
    out.show_entries = {
      ...out.show_entries,
      show_ids: idx.map(i => card.show_entries.show_ids[i]),
      entry_ids: idx.map(i => (card.show_entries.entry_ids || [])[i])
    }
  }
  if (Array.isArray(card.rows)) {
    const ids = new Set(out.entry_ids)
    out.rows = out.rows.filter(r => ids.has(r.entry_id))
  }
  return out
}

// SHO-439: a card's value for one of its shows. Cards still edit class, number, check-in and
// weight at the card level and update the card in place, while `rows` stays as loaded. So when
// every row agrees, the card's own (possibly just edited) value is used, exactly as before;
// only when the rows really differ does each show read its own row. Once cards group by animal
// the rows can differ, and the edits become per-row too.
const ROW_FIELDS = {
  class_id: {get: r => r.class_id, card: c => c.class_id},
  entry_number: {get: r => r.entry_number, card: c => (c.entry_numbers || [])[0]},
  // compared by whether it is set: check-in times always differ between shows
  checked_in: {get: r => r.checked_in, card: c => c.checked_in, same: v => !!v},
  weight: {get: r => (r.entry_details || {}).weight, card: c => (c.entry_details || {}).weight}
}

function rowFor (card, index) {
  const id = (card.entry_ids || [])[index]
  return (card.rows || []).find(r => r.entry_id === id) || null
}

// Whether the card's rows hold more than one value for `field`
export function rowsDiffer (card, field) {
  const f = ROW_FIELDS[field]
  const rows = (card && card.rows) || []
  if (!f || rows.length < 2) return false
  const same = f.same || (v => v === undefined || v === null || v === '' ? null : String(v))
  return new Set(rows.map(r => same(f.get(r)))).size > 1
}

export function showValue (card, index, field) {
  const f = ROW_FIELDS[field]
  if (!f || !card) return undefined
  if (rowsDiffer(card, field)) {
    const row = rowFor(card, index)
    if (row) return f.get(row)
  }
  return f.card(card)
}

// Cards from get_entry_group, ready for the entry lists: duplicate shows split off, and a copy of
// the entry_details the card showed when it loaded. Edit forms change the card's entry_details in
// place, so that copy is how a save tells which fields the user actually changed.
export function prepareEntryGroup (entryGroup) {
  splitDuplicateShows(entryGroup)
  if (entryGroup && Array.isArray(entryGroup.entries)) {
    entryGroup.entries.forEach(card => {
      card.loaded_entry_details = JSON.parse(JSON.stringify(card.entry_details || {}))
      card.loaded_class_id = card.class_id
    })
  }
  return entryGroup
}

// The entry_details to save on the card's entry at `index`: that entry's own details with only
// the fields the user changed on the card applied. The backend replaces entry_details whole, and
// a card only shows its first entry's details, so writing the card's copy to every entry used to
// overwrite the other shows' own values (place, score, ribbon, exhibitor_number, ...).
// Without rows or a load-time copy (older payloads) it falls back to the card's details as before.
export function entryDetailsForIndex (card, index, edited) {
  const row = card ? rowFor(card, index) : null
  const loaded = card ? card.loaded_entry_details : null
  if (!row || !loaded || !edited) return edited
  const out = JSON.parse(JSON.stringify(row.entry_details || {}))
  const keys = new Set([...Object.keys(edited), ...Object.keys(loaded)])
  keys.forEach(k => {
    if (JSON.stringify(edited[k]) === JSON.stringify(loaded[k])) return
    if (k in edited) {
      out[k] = edited[k]
    } else {
      delete out[k]
    }
  })
  return out
}

// After a save succeeds: the entry's row now holds what was written, and the card's load-time copy
// moves to the details the user now sees, so the next save diffs against them.
export function recordEntrySaved (card, entryId, saved) {
  if (!card) return
  const row = (card.rows || []).find(r => r.entry_id === entryId)
  if (row) {
    Object.keys(saved).forEach(k => {
      row[k] = k === 'entry_details' ? JSON.parse(JSON.stringify(saved[k])) : saved[k]
    })
  }
  if (saved.entry_details && card.entry_details) {
    card.loaded_entry_details = JSON.parse(JSON.stringify(card.entry_details))
  }
  // the class the user now sees is the one the next edit compares against (entryEditForShow)
  if ('class_id' in saved && 'loaded_class_id' in card) {
    card.loaded_class_id = card.class_id
  }
}

// Check-in across a card's shows. While the rows agree the card's own checked_in decides, as
// before; once they differ (a merged card, SHO-439) every show must be checked in to count.
export function allShowsCheckedIn (card) {
  if (!card) return false
  if (!rowsDiffer(card, 'checked_in')) return !!card.checked_in
  return (card.entry_ids || []).every((id, i) => !!showValue(card, i, 'checked_in'))
}

// The given entry ids of the card whose show is not checked in yet
export function uncheckedEntryIds (card, entryIds) {
  return (entryIds || []).filter(id => !showValue(card, card.entry_ids.indexOf(id), 'checked_in'))
}

// The card-level checked_in after one show changed: the latest check-in of its rows, or null
// (get_entry_group's card value is MAX(checked_in) too).
export function latestCheckIn (card) {
  const times = ((card && card.rows) || []).map(r => r.checked_in).filter(Boolean)
  if (!times.length) return null
  return times.reduce((a, b) => (new Date(b) > new Date(a) ? b : a))
}

// The card's entry indexes in the event's show order (eventShows), so every card lists its shows
// alike; a show the event doesn't list goes last, keeping the card's order.
export function showIndexesInEventOrder (card, eventShows) {
  const position = {}
  ;(eventShows || []).forEach((s, i) => { position[s.show_id] = i })
  const at = id => (id in position ? position[id] : Infinity)
  const shows = (card && card.shows) || []
  return shows.map((id, i) => i).sort((a, b) => (at(shows[a]) - at(shows[b])) || (a - b))
}

// The card's entry indexes an edit may write to. The backend refuses edits to a checked-in entry,
// so on a card whose shows are checked in separately only the shows not checked in yet are
// edited. While the shows agree every entry is sent, as before (a fully checked-in card still
// gets the backend's refusal rather than a silent no-op).
export function editableEntryIndexes (card) {
  const ids = (card && card.entry_ids) || []
  if (!rowsDiffer(card, 'checked_in')) return ids.map((id, i) => i)
  return ids.map((id, i) => i).filter(i => !showValue(card, i, 'checked_in'))
}

// One entry's own details with `changes` applied: for edits that target a single show.
export function entryDetailsWith (card, index, changes) {
  const row = rowFor(card, index)
  const base = row ? row.entry_details : card.entry_details
  return {...JSON.parse(JSON.stringify(base || {})), ...changes}
}

// SHO-439: the card after one of its shows is removed (that entry deleted): every per-show array
// loses the same index. Card-level values describe the first entry, so when that one goes they
// move to the new first entry. null when it was the card's only entry.
export function removeEntryFromCard (card, entryId) {
  return removeShowIndex(card, ((card && card.entry_ids) || []).indexOf(entryId))
}

// The same, found by the show (an id, or a {value} / {show_id} option) instead of the entry.
export function removeShowFromCard (card, showId) {
  const id = s => (s && typeof s === 'object') ? (s.show_id || s.value) : s
  return removeShowIndex(card, ((card && card.shows) || []).findIndex(s => id(s) === showId))
}

function removeShowIndex (card, i) {
  if (i < 0) return card
  const keep = card.shows.map((s, j) => j).filter(j => j !== i)
  if (!keep.length) return null
  const out = pickIndexes(card, keep)
  const first = rowFor(out, 0)
  if (i === 0 && first) {
    out.class_id = first.class_id
    out.entry_details = JSON.parse(JSON.stringify(first.entry_details || {}))
    if ('loaded_class_id' in out) out.loaded_class_id = first.class_id
    if ('loaded_entry_details' in out) out.loaded_entry_details = JSON.parse(JSON.stringify(first.entry_details || {}))
  }
  return out
}

// SHO-439: after one show's weight is saved, the other shows that could take it: by weight, not
// checked in, and no weight yet (blank or under 1). The checks come from the view.
export function emptyWeightShowIndexes (card, index, {isWeight, isCheckedIn, weightOf}) {
  return ((card && card.shows) || []).map((s, i) => i).filter(i => i !== index &&
    isWeight(i) && !isCheckedIn(i) && !(parseFloat(weightOf(i)) >= 1))
}

// SHO-508: a weight typed on one show copies into the animal's other blank by-weight shows, and
// those copies follow it until they're edited or checked in. The links live on the card in memory
// only (weight_copies: copy's entry id -> source's entry id), so after a reload every weight is its own.

// The show a show's weight is copied from, or -1 when it's its own
export function weightCopySource (card, index) {
  const links = (card && card.weight_copies) || {}
  const ids = (card && card.entry_ids) || []
  const source = links[ids[index]]
  return source ? ids.indexOf(source) : -1
}

// The shows copying their weight from this one
export function weightCopies (card, index) {
  const links = (card && card.weight_copies) || {}
  const ids = (card && card.entry_ids) || []
  const id = ids[index]
  return ids.map((e, i) => i).filter(i => id !== undefined && links[ids[i]] === id)
}

// The card's links with these shows' own weights (no longer copies)
export function withoutWeightCopies (card, indexes) {
  const links = {...((card && card.weight_copies) || {})}
  indexes.forEach(i => { delete links[card.entry_ids[i]] })
  return links
}

// Saving `value` on one show: which other shows get it too, and the card's links afterwards. The
// show typed in becomes its own weight; its copies (not checked in) follow it, and drop the link
// when the weight is cleared; a real weight also fills the blank shows (the view's checks, as in
// emptyWeightShowIndexes), which then copy from it. checks.canWrite(i), when given, limits both to
// the shows this user may write (a superintendent's shows, an exhibitor's editable ones).
export function planWeightSave (card, index, value, checks) {
  const canWrite = checks.canWrite || (() => true)
  const links = withoutWeightCopies(card, [index])
  const following = weightCopies(card, index).filter(i => !checks.isCheckedIn(i) && canWrite(i))
  const valid = parseFloat(value) >= 1
  const fillChecks = {...checks, isWeight: i => checks.isWeight(i) && canWrite(i)}
  const filled = valid ? emptyWeightShowIndexes(card, index, fillChecks).filter(i => following.indexOf(i) < 0) : []
  following.forEach(i => { if (!valid) delete links[card.entry_ids[i]] })
  filled.forEach(i => { links[card.entry_ids[i]] = card.entry_ids[index] })
  return {following, filled, links}
}

// SHO-508: a card's show rows under their class names, in the order the classes first appear
export function groupRowsByName (rows, nameOf) {
  const groups = []
  ;(rows || []).forEach(row => {
    const name = nameOf(row)
    let group = groups.find(g => g.name === name)
    if (!group) {
      group = {name: name, rows: []}
      groups.push(group)
    }
    group.rows.push(row)
  })
  return groups
}

// SHO-508: the one weight several shows share, ignoring shows with none yet ('' when none has
// one), or null when two shows have different weights
export function sharedWeight (weights) {
  const values = Array.from(new Set((weights || []).map(w => (w === null || w === undefined ? '' : String(w).trim())).filter(w => w !== '')))
  return values.length > 1 ? null : (values[0] || '')
}

// SHO-508: the value every one of these has ('' for none), or null when they differ
export function uniformValue (values) {
  const set = new Set((values || []).map(v => (v === null || v === undefined ? '' : String(v).trim())))
  return set.size === 1 ? Array.from(set)[0] : null
}
